import { PageIcon } from "@/components/PageIcon";
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CalendarDays, Plus, ChevronLeft, ChevronRight, X, Check, Ban, Trash2, Loader2 } from "lucide-react";

const iso = (d) => d.toISOString().slice(0, 10);
const fmt = (s) => (s ? new Date(s + "T00:00:00").toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) : "");
const WD = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function HolidaysPage() {
  const [data, setData] = useState({ items: [], can_approve: false, me: null });
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [requesting, setRequesting] = useState(false);
  const [f, setF] = useState({ start_date: "", end_date: "", note: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/holidays").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const days = useMemo(() => {
    const first = new Date(month.getFullYear(), month.getMonth(), 1);
    const startPad = (first.getDay() + 6) % 7; // Monday-first
    const total = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startPad; i++) cells.push(null);
    for (let d = 1; d <= total; d++) cells.push(new Date(month.getFullYear(), month.getMonth(), d));
    return cells;
  }, [month]);

  const holidaysOn = (dateStr) => data.items.filter((h) => h.status !== "declined" && dateStr >= h.start_date && dateStr <= h.end_date);
  const openDay = (ds) => { setF({ start_date: ds, end_date: ds, note: "" }); setRequesting(true); };

  const submit = async () => {
    if (!f.start_date || !f.end_date) { toast.error("Pick start and end dates"); return; }
    setBusy(true);
    try { await api.post("/holidays", f); toast.success("Holiday requested"); setF({ start_date: "", end_date: "", note: "" }); setRequesting(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not submit"); }
    finally { setBusy(false); }
  };
  const decide = async (h, decision) => { try { await api.post(`/holidays/${h.id}/decision`, { decision }); toast.success(`Request ${decision}`); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const cancel = async (h) => { if (!window.confirm("Cancel this holiday request?")) return; try { await api.delete(`/holidays/${h.id}`); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };

  const pending = data.items.filter((h) => h.status === "pending");
  const mine = data.items.filter((h) => h.user_id === data.me);
  const monthLabel = month.toLocaleDateString("en-GB", { month: "long", year: "numeric" });

  return (
    <div className="px-6 lg:px-8 pt-3 lg:pt-4 pb-6 lg:pb-8" data-testid="holidays-page">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-3"><PageIcon /> Holiday Calendar</h1>
          <p className="text-sm text-slate-500 mt-1">Request time off and see who's already booked so you don't overlap.</p>
        </div>
        <Button className="gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => setRequesting(true)} data-testid="holiday-request-btn"><Plus className="w-4 h-4" /> Request holiday</Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Calendar */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="holiday-calendar">
          <div className="flex items-center justify-between mb-4">
            <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500" data-testid="holiday-prev-month"><ChevronLeft className="w-4 h-4" /></button>
            <div className="font-bold text-slate-900">{monthLabel}</div>
            <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500" data-testid="holiday-next-month"><ChevronRight className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-semibold text-slate-400 mb-1">{WD.map((d) => <div key={d}>{d}</div>)}</div>
          {loading ? <div className="py-10 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div> : (
            <div className="grid grid-cols-7 gap-1">
              {days.map((d, i) => {
                if (!d) return <div key={i} />;
                const ds = iso(d);
                const on = holidaysOn(ds);
                return (
                  <button key={i} onClick={() => openDay(ds)} className="min-h-[64px] w-full rounded-lg border border-slate-100 p-1 text-left align-top hover:border-indigo-300 hover:bg-indigo-50/40 cursor-pointer" data-testid={`holiday-day-${ds}`}>
                    <div className="text-[11px] text-slate-400">{d.getDate()}</div>
                    <div className="space-y-0.5 mt-0.5">
                      {on.slice(0, 3).map((h) => (
                        <div key={h.id} title={`${h.user_name}${h.note ? " — " + h.note : ""}`}
                          className={`truncate text-[10px] px-1 py-0.5 rounded ${h.status === "approved" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"} ${h.user_id === data.me ? "ring-1 ring-indigo-400" : ""}`}>
                          {h.user_name.split(" ")[0]}
                        </div>
                      ))}
                      {on.length > 3 && <div className="text-[10px] text-slate-400 px-1">+{on.length - 3} more</div>}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
          <div className="flex items-center gap-3 mt-3 text-[11px] text-slate-500">
            <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-emerald-100 border border-emerald-300" /> Approved</span>
            <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-amber-100 border border-amber-300" /> Pending</span>
            <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded ring-1 ring-indigo-400" /> You</span>
          </div>
        </div>

        {/* Sidebar: approvals + my requests */}
        <div className="space-y-4">
          {data.can_approve && (
            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="holiday-approvals">
              <h2 className="text-base font-bold text-slate-900 mb-3">Pending approvals ({pending.length})</h2>
              {pending.length === 0 ? <div className="text-sm text-slate-400">Nothing awaiting approval.</div> : (
                <div className="space-y-2">
                  {pending.map((h) => (
                    <div key={h.id} className="rounded-xl border border-amber-200 bg-amber-50/50 p-3" data-testid={`holiday-pending-${h.id}`}>
                      <div className="font-medium text-slate-900 text-sm">{h.user_name}</div>
                      <div className="text-xs text-slate-500">{fmt(h.start_date)} → {fmt(h.end_date)}</div>
                      {h.note && <div className="text-xs text-slate-600 mt-1">“{h.note}”</div>}
                      <div className="flex gap-2 mt-2">
                        <Button size="sm" className="h-7 text-xs gap-1 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => decide(h, "approved")} data-testid={`holiday-approve-${h.id}`}><Check className="w-3.5 h-3.5" /> Approve</Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-rose-600" onClick={() => decide(h, "declined")} data-testid={`holiday-decline-${h.id}`}><Ban className="w-3.5 h-3.5" /> Decline</Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="holiday-mine">
            <h2 className="text-base font-bold text-slate-900 mb-3">My requests ({mine.length})</h2>
            {mine.length === 0 ? <div className="text-sm text-slate-400">No holiday requests yet.</div> : (
              <div className="space-y-2">
                {mine.map((h) => (
                  <div key={h.id} className="rounded-xl border border-slate-200 p-3 flex items-center justify-between" data-testid={`holiday-mine-${h.id}`}>
                    <div className="min-w-0">
                      <div className="text-sm text-slate-800">{fmt(h.start_date)} → {fmt(h.end_date)}</div>
                      <span className={`inline-block mt-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${h.status === "approved" ? "bg-emerald-100 text-emerald-700" : h.status === "declined" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>{h.status}</span>
                    </div>
                    <button onClick={() => cancel(h)} className="text-slate-400 hover:text-rose-600" title="Cancel"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {requesting && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex justify-end" onClick={() => setRequesting(false)} data-testid="holiday-form">
          <div className="bg-white h-full w-full sm:w-1/2 sm:max-w-none shadow-2xl overflow-y-auto animate-in slide-in-from-right duration-300" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h3 className="text-xl font-bold text-slate-900">Request Holiday</h3>
              <button onClick={() => setRequesting(false)} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs font-semibold uppercase tracking-wide text-slate-500">From</label>
                  <Input type="date" value={f.start_date} onChange={(e) => setF((s) => ({ ...s, start_date: e.target.value }))} className="mt-1" data-testid="holiday-start" /></div>
                <div><label className="text-xs font-semibold uppercase tracking-wide text-slate-500">To</label>
                  <Input type="date" value={f.end_date} onChange={(e) => setF((s) => ({ ...s, end_date: e.target.value }))} className="mt-1" data-testid="holiday-end" /></div>
              </div>
              <div><label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Note (optional)</label>
                <textarea value={f.note} onChange={(e) => setF((s) => ({ ...s, note: e.target.value }))} rows={3} className="mt-1 w-full rounded-lg border border-slate-200 bg-slate-50 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100" data-testid="holiday-note" /></div>
            </div>
            <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setRequesting(false)}>Cancel</Button>
              <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" disabled={busy} onClick={submit} data-testid="holiday-submit">{busy && <Loader2 className="w-4 h-4 animate-spin mr-1" />}Submit request</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
